import { useMemo } from 'react'
import { useData } from '../contexts/DataContextProvider'
import styles from './Multiples.module.scss'
import MultiplesHeader from './MultiplesHeader'
import MultipleDotBars from './MultipleDotBars'
import { format } from 'd3-format'
import CircleDollarSign from './CircleDollarSign'
import { Color } from '../../../constants'

export function parseAmount(amount: string | number | undefined): number {
  const hasMultipleValues = typeof amount === 'string' && amount.includes(' ')
  const parsedAmount: string | undefined = hasMultipleValues
    ? amount.split(' ')[0]
    : amount?.toString()

  if (typeof parsedAmount === 'string') {
    const parsed = parseFloat(parsedAmount.replace(/[^0-9.-]+/g, ''))
    return isNaN(parsed) ? 0 : parsed
  }
  return 0
}

const formatCommas = format(',d')

export default function InitiativesFunds() {
  const { data, definitions } = useData()

  const fundsCategories = useMemo(() => {
    const placeholderOrg = data.filter(({ initCommTask }) => initCommTask)?.[0]

    const budgetLabel =
      definitions.find(({ id }) => id === 'budget:amount')?.display_label ||
      'Budget Amount'
    const valueLabel =
      definitions.find(({ id }) => id === 'value:amount')?.display_label ||
      'Value Amount'

    const budgetTotal = data
      .filter(({ budget_amount }) => !!budget_amount)
      .reduce(
        (total, { budget_amount }) => total + parseAmount(budget_amount),
        0
      )

    const countBudgeted = Math.ceil(budgetTotal / 1000000)
    const budgetedOrgs = new Array(countBudgeted).fill(placeholderOrg)

    const dispersedTotal = data
      .filter(({ value_amount }) => !!value_amount)
      .reduce((total, { value_amount }) => total + parseAmount(value_amount), 0)

    const countDispersed = Math.ceil(dispersedTotal / 1000000)

    const dispersedOrgs = new Array(countDispersed).fill(placeholderOrg)

    return [
      {
        name: budgetLabel,
        orgs: budgetedOrgs,
        value: `$${formatCommas(budgetTotal)}`,
      },
      {
        name: valueLabel,
        orgs: dispersedOrgs,
        value: `$${formatCommas(dispersedTotal)}`,
      },
    ]
  }, [data, definitions])

  return (
    <div className={`${styles.layout}`}>
      <MultiplesHeader
        title="Initiatives Funds"
        copy={[
          'Millions of dollars in public and private funds have been committed to repair and redress, and disbursements are being made to harmed communities and populations.',
        ]}
      />

      <MultipleDotBars
        dataByMultiple={fundsCategories}
        CircleInstance={CircleDollarSign}
        legendItems={[{ name: '= $1,000,000', fill: Color.Yellow }]}
      />
    </div>
  )
}
